<template>
  <div class="wave-interference-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex><strong>公式：</strong>详见下方原理说明</div>
    </div>

    <div class="canvas-box">
      <svg width="100%" height="400" viewBox="0 0 600 400">
        <!-- 装饰背景 -->
        <rect width="100%" height="100%" fill="#050505" />
        
        <!-- 坐标轴 -->
        <line x1="50" y1="200" x2="550" y2="200" stroke="#333" stroke-dasharray="4,4" />

        <!-- 驻波模式 -->
        <g v-if="mode === 'standing'">
          <!-- 原波 (向右) -->
          <path :d="wavePath(1)" fill="none" stroke="#ff4757" opacity="0.3" stroke-dasharray="5,5" />
          <!-- 反射波 (向左) -->
          <path :d="wavePath(-1)" fill="none" stroke="#00d2ff" opacity="0.3" stroke-dasharray="5,5" />
          <!-- 合成驻波 -->
          <path :d="standingWavePath" fill="none" stroke="#fff" stroke-width="3" filter="url(#glow)" />
          
          <!-- 波节/波腹标注 -->
          <circle v-for="n in 5" :key="'node-'+n" :cx="100 + n * 100" cy="200" r="4" fill="#ff4757" />
          <text x="50" y="380" fill="#888" font-size="12">红色圆点为波节（位移始终为0）</text>
        </g>

        <!-- 干涉模式 -->
        <g v-else>
           <!-- 波源 S1 -->
           <circle cx="100" cy="150" r="8" fill="#ff4757" />
           <text x="90" y="140" fill="#ff4757">S1</text>
           <!-- 波源 S2 -->
           <circle cx="100" cy="250" r="8" fill="#00d2ff" />
           <text x="90" y="275" fill="#00d2ff">S2</text>
           
           <!-- 简化二维波前 -->
           <circle v-for="r in interferenceRays" :key="'r1-'+r" cx="100" cy="150" :r="r" fill="none" stroke="#ff4757" :opacity="0.8 - r/400" stroke-width="1.5" />
           <circle v-for="r in interferenceRays" :key="'r2-'+r" cx="100" cy="250" :r="r" fill="none" stroke="#00d2ff" :opacity="0.8 - r/400" stroke-width="1.5" />
        </g>

        <defs>
          <filter id="glow">
            <feGaussianBlur stdDeviation="2.5" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
      </svg>
    </div>

    <div class="interaction-panel">
       <div class="row">
         <label>波长 (λ):</label>
         <el-slider v-model="wavelength" :min="50" :max="200" size="small"></el-slider>
       </div>
       <div class="row">
         <label>振幅 (A):</label>
         <el-slider v-model="amplitude" :min="10" :max="60" size="small"></el-slider>
       </div>
    </div>

    <div class="theory">
       <p v-if="mode === 'standing'"><strong>驻波</strong>：频率相同的两列对向波叠加。波节静止，波腹振幅最大。</p>
       <p v-else><strong>干涉</strong>：相干波源相遇。加强区位移恒大，减弱区位移恒小。</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WaveInterferenceViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'standing',
      wavelength: 100,
      amplitude: 40,
      time: 0,
      running: true
    }
  },
  computed: {
    interferenceRays() {
      // 模拟扩散的波形
      const baseR = (this.time * 50) % 60;
      return [baseR, baseR + 60, baseR + 120, baseR + 180, baseR + 240, baseR + 300];
    },
    standingWavePath() {
      const segments = 100;
      const k = 2 * Math.PI / this.wavelength;
      const omega = 2; // 角频率
      let d = "M 50 200";
      for (let i = 0; i <= segments; i++) {
        const x = 50 + i * 5;
        // y = 2A sin(kx) cos(wt)
        const val = 2 * this.amplitude * Math.sin(k * (x - 50)) * Math.cos(omega * this.time);
        d += ` L ${x} ${200 - val}`;
      }
      return d;
    }
  },
  mounted() {
    this.animate();
  },
  beforeDestroy() { 
    this.running = false; 
  },
  methods: {
    animate() {
      if (!this.running) return;
      this.time += 0.05;
      requestAnimationFrame(this.animate);
    },
    wavePath(dir) {
       const segments = 100;
       const k = 2 * Math.PI / this.wavelength;
       const omega = 2;
       let d = "M 50 200";
       for (let i = 0; i <= segments; i++) {
         const x = 50 + i * 5;
         // y = A sin(kx -+ wt)
         const val = this.amplitude * Math.sin(k * (x - 50) - dir * omega * this.time);
         d += ` L ${x} ${200 - val}`;
       }
       return d;
    }
  }
}
</script>

<style scoped lang="scss">
.wave-interference-viz { width: 100%; height: 100%; color: #fff; display: flex; flex-direction: column; gap: 15px; }
.viz-header { display: flex; justify-content: space-between; padding: 15px; background: rgba(255,255,255,0.05); border-radius: 8px; }
.canvas-box { background: #000; border-radius: 12px; border: 1px solid #333; flex: 1; }
.interaction-panel { background: rgba(255,255,255,0.03); padding: 20px; border-radius: 12px; .row { display: flex; align-items: center; gap: 20px; label { width: 100px; font-size: 0.9rem; color: #888; } .el-slider { flex: 1; } } }
.theory { padding: 15px; background: rgba(0,210,255,0.05); color: #00d2ff; font-size: 0.9rem; border-radius: 8px; }
</style>
